import {useCallback, useEffect, useState} from 'react';
import queryString from 'query-string';
import {useHistory, useLocation} from 'react-router-dom';
import url from 'url';
import {removeSurvey} from '../../../api/surveys';
import SurveyRow from './Row';
import Filters from './Filters';
import List from '../../common/List';
import ConfirmationPopup from '../../common/popup/ConfirmationPopup';
import {INITIAL_QUERY} from '../../../constants/surveys';
import {useAppDispatch} from '../../../store/hooks';
import {setTitle} from '../../../store/header/header.slice';
import {getSortedParams} from '../../../helpers';
import {SurveyListItem, SurveyToDelete} from '../types';
import {useTranslation} from 'react-i18next';
import {useSnackbarHook} from '../../../hooks/useSnackbar';

const SurveysList = () => {
  const {t} = useTranslation('surveys');
  const history = useHistory();
  const {pathname, search} = useLocation();
  const dispatch = useAppDispatch();
  const [isDeleting, setIsDeleting] = useState(false);
  const [surveyToDelete, setSurveyToDelete] =
    useState<Nullable<SurveyToDelete>>(null);
  const [toggleRefreshPage, setToggleRefreshPage] = useState(false);
  const {addSuccessToast, addErrorToast} = useSnackbarHook();

  const query = {
    ...INITIAL_QUERY,
    ...getSortedParams(queryString.parse(search))
  };

  useEffect(() => {
    dispatch(setTitle('Surveys'));
  }, []);

  const openModal = useCallback((survey: SurveyToDelete) => {
    setSurveyToDelete(survey);
  }, []);

  const closeModal = useCallback(() => {
    setSurveyToDelete(null);
  }, []);

  const updateList = useCallback((contentLength: number) => {
    const currentPage = Number(query?.page);
    const shouldReturnToPreviousPage =
      contentLength && contentLength === 1 && currentPage > 1;

    if (shouldReturnToPreviousPage) {
      query.page = `${currentPage - 1}`;
      return history.push(url.format({pathname, query: query}));
    }

    setToggleRefreshPage((prevState) => !prevState);
  }, [query, pathname]);

  const handleDeleteSurvey = useCallback(async () => {
    if (!surveyToDelete) {
      return;
    }

    setIsDeleting(true);

    const {id, contentLength, patientName} = surveyToDelete;

    try {
      await removeSurvey(`${id}`);
      addSuccessToast(t('toasts.removeSurveyMessage', {patientName}));
      updateList(contentLength);
    } catch (e) {
      addErrorToast(e?.message);
    } finally {
      setIsDeleting(false);
      setSurveyToDelete(null);
    }
  }, [surveyToDelete, updateList]);

  return (
    <>
      <List
        rowRenderer={(content) => content.map((survey: SurveyListItem) => (
          <SurveyRow
            key={`${survey.surveyId}`}
            survey={survey}
            contentLength={content.length}
            openModal={openModal}
          />
        ))}
        filters={<Filters />}
        toggleRefreshPage={toggleRefreshPage}
        initialQuery={INITIAL_QUERY}
      />

      <ConfirmationPopup
        isActionProcessing={isDeleting}
        labels={{body: surveyToDelete?.message || ''}}
        isOpen={!!surveyToDelete}
        setIsOpen={closeModal}
        confirmableAction={handleDeleteSurvey}
      />
    </>
  );
};

export default SurveysList;
